import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Eye, EyeOff, Loader2, Clock, CheckCircle2, ArrowLeft, ArrowRight, Sparkles, Zap, ShieldCheck, Check, X, Users, Hotel, Receipt, Ticket, MailCheck } from 'lucide-react';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const heading = { fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" };

const CATEGORIES = [
  {
    key: 'hr',
    label: 'StayMii HR',
    sub: 'Free public job application form + applicant tracking',
    icon: Users,
    accent: 'emerald',
    badge: 'FREE · INSTANT ACCESS',
    autoApproved: true,
  },
  {
    key: 'pms',
    label: 'StayMii Lite',
    sub: 'PMS, channel manager & booking engine',
    icon: Hotel,
    accent: 'blue',
    badge: 'ADMIN APPROVAL',
    autoApproved: false,
  },
  {
    key: 'einvoice',
    label: 'StayMii e-Invoice',
    sub: 'LHDN MyInvois automation & compliance',
    icon: Receipt,
    accent: 'indigo',
    badge: 'ADMIN APPROVAL',
    autoApproved: false,
  },
  {
    key: 'eticket',
    label: 'StayMii e-Ticket',
    sub: 'Sell tickets online & scan QR at the gate',
    icon: Ticket,
    accent: 'cyan',
    badge: 'VERIFY + APPROVAL',
    autoApproved: false,
  },
];

export default function UnifiedSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCategory = (searchParams.get('category') || searchParams.get('from') || '').toLowerCase();
  // Capture & persist referral source (e.g. ?ref=beds24) so partner commissions are attributed.
  useEffect(() => {
    const ref = (searchParams.get('ref') || searchParams.get('utm_source') || '').trim();
    if (ref) sessionStorage.setItem('staymii_referral', ref.toLowerCase());
  }, [searchParams]);
  const [category, setCategory] = useState(
    ['hr', 'pms', 'einvoice', 'eticket'].includes(initialCategory) ? initialCategory : ''
  );
  const [form, setForm] = useState({ email: '', username: '', password: '', phone: '', property_name: '', room_count: '', property_country: 'MY', property_state: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [autoApproved, setAutoApproved] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resent, setResent] = useState(false);
  const [doneCategory, setDoneCategory] = useState('');
  const [acceptedPlatform, setAcceptedPlatform] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // ── Live username availability check (debounced 400ms) ──
  const [uname, setUname] = useState({ checking: false, valid: null, available: null, reason: '', suggestions: [] });
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const u = form.username.trim();
    if (u.length === 0) {
      setUname({ checking: false, valid: null, available: null, reason: '', suggestions: [] });
      return;
    }
    setUname((p) => ({ ...p, checking: true }));
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/auth/unified/check-username`, { params: { username: u } });
        setUname({ checking: false, ...r.data });
      } catch {
        setUname({ checking: false, valid: null, available: null, reason: '', suggestions: [] });
      }
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [form.username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (!category) {
        setError('Please select a category (HR, Lite, e-Invoice or e-Ticket)');
        setLoading(false);
        return;
      }
      const noProperty = category === 'hr' || category === 'eticket';
      if (category === 'eticket' && (!acceptedPlatform || !acceptedPrivacy)) {
        setError('Please accept the eTicket Platform Terms of Use and the Privacy Policy to continue.');
        setLoading(false);
        return;
      }
      const res = await axios.post(`${API_URL}/api/auth/unified/register`, {
        email: form.email,
        username: form.username,
        password: form.password,
        phone: form.phone,
        property_name: noProperty ? '' : form.property_name,
        room_count: noProperty ? '' : form.room_count,
        property_country: noProperty ? '' : form.property_country,
        property_state: noProperty ? '' : form.property_state,
        modules: [category],
        origin_url: window.location.origin,
        referral_source: sessionStorage.getItem('staymii_referral') || undefined,
        accepted_platform_terms: category === 'eticket' ? acceptedPlatform : undefined,
        accepted_privacy: category === 'eticket' ? acceptedPrivacy : undefined,
      });
      setAutoApproved(!!res.data?.auto_approved);
      setNeedsVerification(!!res.data?.needs_verification);
      setDoneCategory(res.data?.category || category);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  /* ─── SUCCESS STATE ─── */
  if (done) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <LandingNavBar />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center">
            {needsVerification ? (
              <>
                <div className="mx-auto w-16 h-16 bg-cyan-50 rounded-2xl flex items-center justify-center mb-6">
                  <MailCheck className="w-8 h-8 text-cyan-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="verify-email-card">
                  Check your email
                </h2>
                <p className="text-slate-500 mb-6 leading-relaxed">
                  We've sent a verification link to <strong className="text-slate-700">{form.email}</strong>. Click it to verify your address.
                  After verification, your account will be reviewed by our team before it's activated.
                </p>
                <p className="text-xs text-slate-400 mb-6">Didn't get it? Check your spam folder, or resend below.</p>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/api/auth/unified/resend-verification`, { email: form.email, origin_url: window.location.origin });
                      setError('');
                      setResent(true);
                    } catch { /* generic */ setResent(true); }
                  }}
                  className="w-full h-12 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-[#0B1120] font-semibold transition-colors mb-3"
                  data-testid="resend-verification-btn"
                >
                  {resent ? 'Verification email re-sent ✓' : 'Resend verification email'}
                </button>
                <Link to="/auth/login" className="block text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2" data-testid="goto-login-after-verify">
                  Back to login
                </Link>
              </>
            ) : autoApproved && doneCategory === 'hr' ? (
              <>
                <div className="mx-auto w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="hr-ready-card">
                  Your StayMii HR account is live!
                </h2>
                <p className="text-slate-500 mb-6 leading-relaxed">
                  No admin approval needed for HR. Log in now, register your company, and your public
                  job application form will be ready in under a minute.
                </p>
                <Link to="/auth/login">
                  <Button className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="goto-login-hr">
                    Log in & create my form <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/hr/apply" className="block mt-3 text-sm text-emerald-700 hover:text-emerald-900 font-semibold underline underline-offset-2" data-testid="goto-sample-form-after-signup">
                  Or preview the live sample form first →
                </Link>
              </>
            ) : autoApproved ? (
              <>
                <div className="mx-auto w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="einvoice-ready-card">
                  You're all set!
                </h2>
                <p className="text-slate-500 mb-6 leading-relaxed">
                  Your StayMii e-Invoice account is active. Log in and start issuing LHDN-compliant invoices right away.
                </p>

                {/* ── 30-day free trial upsell ── */}
                <div
                  className="relative mb-6 rounded-2xl p-5 text-left bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/20 overflow-hidden"
                  data-testid="trial-upsell-card"
                >
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-2xl" aria-hidden="true" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-yellow-300" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-yellow-200">
                        Limited offer · New accounts only
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold leading-tight mb-1" style={heading}>
                      Try Growth free for 14 days
                    </h3>
                    <p className="text-sm text-white/80 leading-relaxed mb-3">
                      Unlock unlimited invoices, direct LHDN MyInvois submission, and priority support — free for your first 14 days.
                    </p>
                    <ul className="text-xs text-white/85 space-y-1.5 mb-4">
                      <li className="flex items-start gap-2">
                        <Zap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-yellow-300" />
                        <span>Unlimited e-invoices</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Zap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-yellow-300" />
                        <span>Direct LHDN MyInvois API submission</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-yellow-300" />
                        <span>Priority WhatsApp + email support</span>
                      </li>
                    </ul>
                    <Link to="/pricing?trial=einvoice&from=signup">
                      <Button
                        className="w-full h-11 rounded-xl bg-white text-indigo-700 hover:bg-slate-50 font-semibold shadow-sm"
                        data-testid="start-free-trial-btn"
                      >
                        Start 14-day free trial <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <p className="text-[11px] text-white/60 mt-2 text-center">
                      No credit card required · Cancel anytime
                    </p>
                  </div>
                </div>

                <Link to="/auth/login">
                  <Button
                    variant="ghost"
                    className="w-full h-11 rounded-xl text-slate-600 hover:bg-slate-50"
                    data-testid="goto-login-after-signup"
                  >
                    Skip — just log in to free account
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <div className="mx-auto w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-6">
                  <Clock className="w-8 h-8 text-amber-500" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="pending-card">
                  Account Created
                </h2>
                <p className="text-slate-500 mb-8 leading-relaxed">
                  Your account is pending approval. You will receive an email once your account has been approved.
                </p>
                <Link to="/auth/login">
                  <Button className="w-full h-12 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="goto-login-after-signup">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
        <LandingFooter />
      </div>
    );
  }

  /* ─── MAIN SIGNUP ─── */
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <LandingNavBar />

      <div className="flex-1 flex flex-col lg:flex-row pt-20">
        {/* ── LEFT: FORM ── */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 xl:p-24">
          <div className="w-full max-w-md">
            {/* Branding */}
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-3">
                <p className="text-xs font-semibold text-[#2563EB] tracking-[0.15em] uppercase">Get started</p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200" data-testid="signup-time-badge">
                  <Clock className="w-3 h-3" /> ≈ 30 seconds
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mb-3" style={heading}>
                Create your<br />account
              </h1>
              <p className="text-slate-600 text-base leading-relaxed">
                One free account unlocks <strong className="text-emerald-700">StayMii HR</strong> (public job application
                form + applicant tracking), <strong className="text-slate-800">StayMii Lite</strong> (hotel PMS &amp;
                channel manager), <strong className="text-slate-800">StayMii e-Invoice</strong> (LHDN MyInvois automation), and <strong className="text-slate-800">StayMii e-Ticket</strong> (sell &amp; scan event tickets).
              </p>
              <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> HR signups are <strong>auto-approved</strong> — log in instantly.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-card">
              {error && (
                <Alert variant="destructive" className="bg-red-50 border-red-200 rounded-xl">
                  <AlertDescription className="text-red-600 text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-3">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Choose Your Category</Label>
                <div className="space-y-2.5">
                  {CATEGORIES.map(c => {
                    const selected = category === c.key;
                    const accentBorder = c.accent === 'emerald'
                      ? (selected ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-emerald-300')
                      : c.accent === 'blue'
                        ? (selected ? 'border-[#2563EB] bg-[#2563EB]/5 ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-300')
                        : c.accent === 'cyan'
                          ? (selected ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-200' : 'border-slate-200 hover:border-cyan-300')
                          : (selected ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-indigo-300');
                    const iconBg = c.accent === 'emerald' ? 'bg-emerald-100 text-emerald-700' : c.accent === 'blue' ? 'bg-blue-100 text-blue-700' : c.accent === 'cyan' ? 'bg-cyan-100 text-cyan-700' : 'bg-indigo-100 text-indigo-700';
                    const badgeColor = c.autoApproved
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                      : 'bg-amber-100 text-amber-700 border border-amber-300';
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setCategory(c.key)}
                        className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all bg-white ${accentBorder}`}
                        data-testid={`category-${c.key}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
                          <c.icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-slate-900">{c.label}</p>
                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${badgeColor}`}>{c.badge}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{c.sub}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 shrink-0 mt-1 flex items-center justify-center ${selected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {category === 'hr' && (
                  <p className="text-xs text-emerald-700 flex items-center gap-1.5 pt-1" data-testid="hr-auto-approve-note">
                    <Sparkles className="w-3.5 h-3.5" /> StayMii HR signups skip admin approval — log in immediately after registering.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                  autoFocus
                  placeholder="you@company.com"
                  className="h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]"
                  data-testid="signup-email"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Username</Label>
                <div className="relative">
                  <Input
                    value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value.replace(/[^a-zA-Z0-9]/g, '') })}
                    required
                    minLength={5}
                    maxLength={30}
                    pattern="[a-zA-Z0-9]{5,30}"
                    title="Letters and numbers only — at least 5 characters, no spaces or symbols"
                    placeholder="e.g. marilyn86"
                    className={`h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 focus:ring-1 pr-12 ${
                      uname.valid === false || uname.available === false
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                        : uname.available === true
                          ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-200'
                          : 'focus:border-[#2563EB] focus:ring-[#2563EB]'
                    }`}
                    data-testid="signup-username"
                    aria-describedby="username-hint"
                  />
                  <div className="absolute inset-y-0 right-4 flex items-center">
                    {uname.checking && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                    {!uname.checking && uname.available === true && <Check className="w-5 h-5 text-emerald-500" data-testid="username-ok" />}
                    {!uname.checking && (uname.valid === false || uname.available === false) && <X className="w-5 h-5 text-red-500" data-testid="username-bad" />}
                  </div>
                </div>
                <div id="username-hint" className="min-h-[1.25rem] text-xs">
                  {form.username && uname.valid === false && (
                    <span className="text-red-600">{uname.reason}</span>
                  )}
                  {form.username && uname.valid && uname.available === false && (
                    <span className="text-red-600">
                      That username is already taken.
                      {uname.suggestions && uname.suggestions.length > 0 && (
                        <>
                          {' '}Try:{' '}
                          {uname.suggestions.map((s, i) => (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setForm(f => ({ ...f, username: s }))}
                              className="underline underline-offset-2 text-red-700 hover:text-red-900 font-medium"
                              data-testid={`username-suggestion-${i}`}
                            >
                              {s}
                            </button>
                          )).reduce((acc, x, i) => acc === null ? [x] : [...acc, <span key={`sep-${i}`} className="text-red-500">{', '}</span>, x], null)}
                        </>
                      )}
                    </span>
                  )}
                  {form.username && uname.available === true && (
                    <span className="text-emerald-600">Username is available.</span>
                  )}
                  {!form.username && (
                    <span className="text-slate-400">Letters and numbers only — minimum 5 characters, no spaces or symbols.</span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Phone Number</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+60123456789"
                  className="h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]"
                  data-testid="signup-phone"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Password</Label>
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Create a password"
                    className="h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 pr-12 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]"
                    data-testid="signup-password"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400">Minimum 8 characters</p>
              </div>

              {/* Property Details — hidden when HR-only signup since HR serves non-property businesses too */}
              {category !== 'hr' && category !== 'eticket' && (
                <>
                <div className="pt-2 pb-1">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Property Details</Label>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Property Name</Label>
                <Input
                  value={form.property_name}
                  onChange={e => setForm({ ...form, property_name: e.target.value })}
                  placeholder="e.g. ABC Hotel"
                  className="h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]"
                  data-testid="signup-property-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">How many rooms do you have?</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.room_count}
                  onChange={e => setForm({ ...form, room_count: e.target.value })}
                  placeholder="e.g. 25"
                  className="h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]"
                  data-testid="signup-room-count"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Property Country</Label>
                  <select
                    value={form.property_country}
                    onChange={e => setForm({ ...form, property_country: e.target.value })}
                    className="w-full h-14 rounded-xl bg-[#F9F9F8] border border-slate-200 text-slate-900 text-base px-3 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] focus:outline-none"
                    data-testid="signup-property-country"
                  >
                    <option value="MY">Malaysia</option>
                    <option value="SG">Singapore</option>
                    <option value="TH">Thailand</option>
                    <option value="ID">Indonesia</option>
                    <option value="PH">Philippines</option>
                    <option value="VN">Vietnam</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Property State</Label>
                  {form.property_country === 'MY' ? (
                    <select
                      value={form.property_state}
                      onChange={e => setForm({ ...form, property_state: e.target.value })}
                      className="w-full h-14 rounded-xl bg-[#F9F9F8] border border-slate-200 text-slate-900 text-base px-3 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] focus:outline-none"
                      data-testid="signup-property-state"
                    >
                      <option value="">Select state</option>
                      <option value="Johor">Johor</option>
                      <option value="Kedah">Kedah</option>
                      <option value="Kelantan">Kelantan</option>
                      <option value="Melaka">Melaka</option>
                      <option value="Negeri Sembilan">Negeri Sembilan</option>
                      <option value="Pahang">Pahang</option>
                      <option value="Pulau Pinang">Pulau Pinang</option>
                      <option value="Perak">Perak</option>
                      <option value="Perlis">Perlis</option>
                      <option value="Selangor">Selangor</option>
                      <option value="Terengganu">Terengganu</option>
                      <option value="Sabah">Sabah</option>
                      <option value="Sarawak">Sarawak</option>
                      <option value="Kuala Lumpur">Kuala Lumpur</option>
                      <option value="Labuan">Labuan</option>
                      <option value="Putrajaya">Putrajaya</option>
                    </select>
                  ) : (
                    <Input
                      value={form.property_state}
                      onChange={e => setForm({ ...form, property_state: e.target.value })}
                      placeholder="State / Province"
                      className="h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]"
                      data-testid="signup-property-state"
                    />
                  )}
                </div>
              </div>
              </>
              )}

              {category === 'eticket' && (
                <div className="space-y-3 pt-1" data-testid="eticket-consent">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={acceptedPlatform}
                      onChange={e => setAcceptedPlatform(e.target.checked)}
                      className="mt-0.5 h-5 w-5 rounded border-slate-300 text-[#0891b2] focus:ring-[#0891b2] cursor-pointer shrink-0"
                      data-testid="accept-platform-terms"
                    />
                    <span className="text-sm text-slate-600 leading-relaxed">
                      I have read and agree to the{' '}
                      <Link to="/legal/eticket-platform-terms" target="_blank" className="text-[#0891b2] font-semibold hover:underline">StayMii eTicket Platform Terms of Use</Link>.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={acceptedPrivacy}
                      onChange={e => setAcceptedPrivacy(e.target.checked)}
                      className="mt-0.5 h-5 w-5 rounded border-slate-300 text-[#0891b2] focus:ring-[#0891b2] cursor-pointer shrink-0"
                      data-testid="accept-privacy"
                    />
                    <span className="text-sm text-slate-600 leading-relaxed">
                      I have read and agree to the{' '}
                      <Link to="/legal/privacy-policy" target="_blank" className="text-[#0891b2] font-semibold hover:underline">StayMii Privacy Policy</Link>.
                    </span>
                  </label>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || uname.checking || uname.valid === false || uname.available === false || (category === 'eticket' && (!acceptedPlatform || !acceptedPrivacy))}
                className="w-full h-14 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-base font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="signup-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Create Account <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>

              <p className="text-center text-xs text-slate-400">No credit card required</p>

              <p className="text-center text-slate-500 text-sm pt-1">
                Already have an account?{' '}
                <Link to="/auth/login" className="text-[#2563EB] hover:text-[#1D4ED8] font-semibold transition-colors" data-testid="goto-login">
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        </div>

        {/* ── RIGHT: IMAGE ── */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <img
            src={category === 'hr'
              ? 'https://customer-assets.emergentagent.com/job_d5d2a817-6920-419a-9713-eaf4c30fc60a/artifacts/v176d9eo_ChatGPT%20Image%20May%2020%2C%202026%2C%2012_36_00%20PM.png'
              : 'https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/ysrm8ch1_generate_images_for_staymii.com.jpeg'}
            alt={category === 'hr' ? 'StayMii HR — Hire, Manage, Grow Together. Free HR platform for Malaysian SMEs' : 'Property owner reviewing compliance documents'}
            className={`absolute inset-0 w-full h-full ${category === 'hr' ? 'object-cover object-top bg-white' : 'object-cover'}`}
          />
          {category !== 'hr' && (
            <>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-10">
                <p className="text-white/60 text-xs font-semibold tracking-[0.15em] uppercase mb-3">StayMii Platform</p>
                <h2 className="text-3xl font-light text-white tracking-tight leading-snug" style={heading}>
                  Start Your<br />Compliance Journey
                </h2>
                <p className="text-white/70 text-sm mt-3 max-w-sm leading-relaxed">
                  From homestays to hotels — we guide every operator through seamless
                  e-invoicing, LHDN MyInvois compliance, channel management, and free HR recruitment.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}
